import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { acceptCommunityConsent, COMMUNITY_CONSENT_EVENT, hasCommunityConsent } from '../lib/api';
import { useModalFocus } from '../lib/useModalFocus';

export function useParticipationConsent(): [boolean, () => boolean] {
  const [allowed, setAllowed] = useState(hasCommunityConsent);
  useEffect(() => {
    const sync = () => setAllowed(hasCommunityConsent());
    window.addEventListener(COMMUNITY_CONSENT_EVENT, sync);
    window.addEventListener('coyg:local-data-cleared', sync);
    return () => {
      window.removeEventListener(COMMUNITY_CONSENT_EVENT, sync);
      window.removeEventListener('coyg:local-data-cleared', sync);
    };
  }, []);
  const accept = () => {
    const persisted = acceptCommunityConsent();
    setAllowed(persisted);
    return persisted;
  };
  return [allowed, accept];
}

export const ParticipationGate = ({
  onAccepted,
  onClose,
  onComplete,
  onSuccessFocus,
  action = 'join in',
}: {
  onAccepted: () => boolean;
  onClose: () => void;
  onComplete?: () => void;
  onSuccessFocus?: () => void;
  action?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useModalFocus(ref, onClose);

  return createPortal(
    <div className="participation-sheet-backdrop" onClick={onClose}>
      <div
        ref={ref}
        className="participation-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="participation-sheet-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <span className="participation-sheet-kicker">North Bank participation</span>
        <h2 id="participation-sheet-title">One quick check before you {action}</h2>
        <p>COYG is open to browse. Posting and supporter reactions are currently for adults who accept the community rules.</p>
        <label className="participation-sheet-check">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.currentTarget.checked)} />
          <span>I’m 18+ and agree to the <a href="/terms.html" target="_blank" rel="noreferrer">Terms</a> and <a href="/community-safety.html" target="_blank" rel="noreferrer">community rules</a>.</span>
        </label>
        {error && <p className="participation-sheet-error" role="alert">{error}</p>}
        <div className="participation-sheet-actions">
          <button type="button" className="ghost-btn" onClick={onClose}>Not now</button>
          <button
            type="button"
            className="primary-btn"
            disabled={!confirmed}
            onClick={() => {
              if (onAccepted()) {
                onComplete?.();
                onClose();
                if (onSuccessFocus) requestAnimationFrame(onSuccessFocus);
              } else setError('COYG could not save that choice on this device. Check private-storage settings and try again; browsing remains open.');
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
